import { IStyle } from "./../Types/Style";
import idx from "idx";
import * as R from "ramda";

import { IDisplayInfoResponse, IResolveResponse, ResolvableType } from "../api/ResolveApi/resolve-api.types";
import {
  IClassGoalResponse,
  IOrganizationAliases,
  IScormSettings,
  IUserAccountInfo,
  ScormPackageState,
} from "../App/types/common";
import {
  ICourseLocalizedText,
  ICourseSet,
  IFullCourse,
  ILocalizedListItemAnswer,
  IPublicationInfo,
} from "../components/course-author/types";
import { PreferredLocale } from "../utils/locales";
import { IAccessData, parseClientFeatures, parseLocale } from "./login";
import { IUserAccount } from "../App/types/common";

export function isErrorRecord(raw: any): boolean {
  return raw.code === undefined || raw.code >= 400;
}

export function hasErrorRecords(rawArray: any[]) {
  return rawArray.find(isErrorRecord) !== undefined;
}

export interface IAccountUIData extends IAccessData {
  displayName: string;
  emailAddresses: string[];
  ownerOrganizationId: string;
  preferredLocale: PreferredLocale;
  primaryEmailAddress: string | null;
}

export function resolveAccountPostSuccessResponseValueToLoginUIData(data: any, extra?: any): IAccountUIData {
  const rawClientFeatures = idx(data, d => d.value.state.oldClientFeatures);

  return {
    displayName: idx(data, d => d.value.state.displayName),
    emailAddresses: idx(data, d => d.value.state.emailAddresses.elems),
    ownerOrganizationId: idx(data, d => d.value.state.owner),
    preferredLocale: idx(data, d => d.value.state.preferredLocale),
    primaryEmailAddress: idx(data, d => d.value.state.primaryEmailAddress),
    ...parseClientFeatures(rawClientFeatures),
  };
}

export interface IOrganizationUIData {
  displayName: string;
  defaultLocale: PreferredLocale | null;
  releaseLine: string;
  ownerOrganizationId: string;
}

export function resolveOrganizationPostSuccessResponseValueToOrganizationUIData(
  data: any,
  extra?: any
): IOrganizationUIData {
  return {
    defaultLocale: parseLocale(idx(data, d => d.value.state.defaultLocale)),
    displayName: idx(data, d => d.value.state.displayName),
    ownerOrganizationId: idx(data, d => d.value.state.owner),
    releaseLine: idx(data, d => d.value.state.releaseLine),
  };
}

export interface ICourseUIData {
  createdAt: string;
  displayName: string;
  locked: boolean;
  masterLocale: PreferredLocale;
  publicationInfo: IPublicationInfo | null;
  supportedLocales: PreferredLocale[];
  lastEditorName: string;
  latestRevId: number;
}

export interface ICourseSummary {
  createdAt: string;
  courseTitle: string;
  lastEditorName: string;
  latestRevId: number;
  locked: boolean;
  localeSettings: {
    masterLocale: PreferredLocale;
    supportedLocales: PreferredLocale[];
  };
  publicationInfo: IPublicationInfo | null;
}

export function resolveCoursePostSuccessResponseValueToCourseUIData(data: any, extra?: any): ICourseUIData {
  return {
    createdAt: idx(data, d => d.value.createdAt),
    displayName: idx(data, d => d.value.courseTitle),
    lastEditorName: idx(data, d => d.value.lastEditorName),
    latestRevId: idx(data, d => d.value.latestRevId),
    locked: idx(data, d => d.value.locked),
    masterLocale: idx(data, d => d.value.localeSettings.masterLocale),
    publicationInfo: idx(data, d => d.value.publicationInfo),
    supportedLocales: idx(data, d => d.value.localeSettings.supportedLocales),
  };
}

export const filterSuccessfulResponses = <T>(
  ids: string[],
  resolvedValues: Array<IResolveResponse<T>>
): Array<R.KeyValuePair<string, IResolveResponse<T>>> =>
  R.filter(([_, value]) => value.code === 200, R.zip(ids, resolvedValues));

export function resolveFullCoursePostSuccessResponseValueToCourseData(
  data: Array<{ value: IFullCourse }>
): IFullCourse | null {
  return idx(data, d => d[0].value) || null;
}
export function resolveUsersPostSuccessResponseValueToUserUIData(userId: string, data: any): IUsersUIData {
  return {
    canonicalAlias: idx(data, d => d.value.canonicalAlias),
    deleted: idx(data, d => d.value.deleted),
    displayName: idx(data, d => d.value.displayName),
    id: userId,
    locked: idx(data, d => d.value.locked),
    owner: idx(data, d => d.value.owner),
  };
}
export interface ICourseInstanceUIData {
  courseId: string;
  completed: boolean;
}

export function resolveCourseInstancePostSuccessResponseValueToCourseInstanceUIData(
  data: any,
  extra?: any
): ICourseInstanceUIData {
  return {
    completed: idx(data, d => d.value.completed),
    courseId: idx(data, d => d.value.courseId),
  };
}

export interface IUsersUIData extends IDisplayInfoResponse {
  id: string;
}

export interface IFullClass {
  admins: string[];
  deleted: boolean;
  displayName: string;
  description: string;
  goals: {
    elems: IClassGoalResponse[];
    nextId: number;
  };
  members: string[];
  owner: string;
}

export interface IFullStyle {
  assets: ICourseSet<string>;
  bodyFontFamilyId: Nullable<string>;
  course: Nullable<string>;
  deleted: boolean;
  displayName: string;
  headerFontFamilyId: Nullable<string>;
  lessCode: string;
  logo: Nullable<string>;
  logoImageId: Nullable<string>;
  monospaceFontFamilyId: Nullable<string>;
  owner: string;
}

export interface IFullOrganization {
  admins: string[];
  style: string | null;
  defaultLocale: PreferredLocale;
  deleted: boolean;
  displayName: string | null;
  owner: string;
  releaseLine: string;
  scormSettings: IScormSettings;
  storeLearnerLog: boolean;
  aliases: IOrganizationAliases;
  useSimpleHtmlClient: boolean;
}

export type IOrganizationState = IStateResponse<IFullOrganization>;

export type IStyleResponse = IStateResponse<IFullStyle>;

export type IClassResponse = IStateResponse<IFullClass>;

export type IUserAccountResponse = IStateResponse<IUserAccount>;

export type IUserAccountInfoResponse = IStateResponse<IUserAccountInfo>;

export interface IStateResponse<T> {
  state: T;
  versionId: number;
}

export interface ICreationResponse<T> {
  value: string;
  bindings: {
    [key: string]: IStateResponse<T>;
  };
}

export interface IClassUIData {
  admins: string[];
  displayName: string;
  courseIds: string[];
  goals: {
    elems: IClassGoalResponse[];
    nextId: number;
  };
  members: string[];
  versionId: number;
}

export function resolveClassPostSuccessResponseValueToClassUIData(
  data: IResolveResponse<IClassResponse>
): IClassUIData {
  const goals: IClassGoalResponse[] = idx(data, d => d.value.state.goals.elems) || [];
  const nextId: number = idx(data, d => d.value.state.goals.nextId) || 0;
  return {
    admins: idx(data, d => d.value.state.admins) as string[],
    courseIds: (idx(data, d => d.value.state.goals.elems) as any[]).map((elem: any) => elem.course),
    displayName: idx(data, d => d.value.state.displayName) as string,
    goals: {
      elems: goals,
      nextId,
    },
    members: idx(data, d => d.value.state.members) as string[],
    versionId: idx(data, d => d.value.versionId) as number,
  };
}

export interface IFullScormPackage {
  completedExitText: ICourseSet<ILocalizedListItemAnswer>;
  course: string;
  courseInstEnv: {
    allowCourseReview: boolean;
    assumePriorKnowledge: boolean;
    includeBranding: boolean;
    useStrictMode: boolean;
  };
  deleted: boolean;
  expectedNumUsers: number | null;
  hideLmsNavbar: boolean;
  inProgressExitText: ICourseSet<ILocalizedListItemAnswer>;
  lms: string;
  locked: boolean;
  name: string;
  owner: string;
  primaryKey: string;
  releaseLine: string;
  scormVersion: string;
  state: string;
  targetOrg: string;
  useCustomCompletedExitText: boolean;
  useCustomInProgressExitText: boolean;
}

export interface IScormPackageUIData {
  courseId: string;
  completedExitText: ICourseSet<ICourseLocalizedText>;
  expectedNumUsers: number | null;
  hideLmsNavbar: boolean;
  inProgressExitText: ICourseSet<ICourseLocalizedText>;
  name: string;
  lms: string;
  locked: boolean;
  scormVersion: string;
  state: ScormPackageState;
  useCustomCompletedExitText: boolean;
  useCustomInProgressExitText: boolean;
  versionId: number;
  includeBranding: boolean;
  allowCourseReview: boolean;
  useStrictMode: boolean;
}

export function toTuple(id: string): [ResolvableType, string] {
  return [R.take(3, id) as ResolvableType, R.drop(4, id)];
}

export function resolveScormPackagePostSuccessResponseValueToScormPackageUIData(
  data: any,
  extra?: any
): IScormPackageUIData {
  return {
    allowCourseReview: idx(data, d => d.value.state.courseInstEnv.allowCourseReview),
    completedExitText: idx(data, d => d.value.state.completedExitText),
    courseId: idx(data, d => d.value.state.course),
    expectedNumUsers: idx(data, d => d.value.state.expectedNumUsers),
    hideLmsNavbar: idx(data, d => d.value.state.hideLmsNavbar),
    inProgressExitText: idx(data, d => d.value.state.inProgressExitText),
    includeBranding: idx(data, d => d.value.state.courseInstEnv.includeBranding),
    lms: idx(data, d => d.value.state.lms),
    locked: idx(data, d => d.value.state.locked),
    name: idx(data, d => d.value.state.name),
    scormVersion: idx(data, d => d.value.state.scormVersion),
    state: idx(data, d => d.value.state.state),
    useCustomCompletedExitText: idx(data, d => d.value.state.useCustomCompletedExitText),
    useCustomInProgressExitText: idx(data, d => d.value.state.useCustomInProgressExitText),
    useStrictMode: idx(data, d => d.value.state.courseInstEnv.useStrictMode),
    versionId: idx(data, d => d.value.versionId),
  };
}

export function resolveStylePostSuccessResponseValueToStyle(
  styleId: string,
  data: IResolveResponse<IStyleResponse>
): IStyle {
  return {
    bodyFontFamilyId: data.value.state.bodyFontFamilyId,
    displayName: data.value.state.displayName,
    headerFontFamilyId: data.value.state.headerFontFamilyId,
    lessCode: data.value.state.lessCode,
    logoImageId: data.value.state.logoImageId,
    monospaceFontFamilyId: data.value.state.monospaceFontFamilyId,
    styleId,
    versionId: data.value.versionId,
  };
}
